import React from 'react';
import { Card, Row, Col, Progress, Typography } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import ChartContainer from '../../components/shared/ChartContainer';
import AnalyticsCard from '../../components/shared/AnalyticsCard';
import { mockRevenueData, mockRevenueForecasting } from '../data/mockData';

const { Text } = Typography;

const ForecastingChart: React.FC = () => {
  const { forecasting } = mockRevenueData;

  // Forecasting chart data
  const forecastingData = {
    labels: mockRevenueForecasting.map(item => item.month),
    datasets: [
      {
        label: 'Projected Revenue ($)',
        data: mockRevenueForecasting.map(item => item.projected),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        fill: true,
      },
      {
        label: 'Upper Bound ($)',
        data: mockRevenueForecasting.map(item => item.upper),
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82, 196, 26, 0.1)',
        borderDash: [5, 5],
        fill: false,
      },
      {
        label: 'Lower Bound ($)',
        data: mockRevenueForecasting.map(item => item.lower),
        borderColor: '#f5222d',
        backgroundColor: 'rgba(245, 34, 45, 0.1)',
        borderDash: [5, 5],
        fill: false,
      },
    ],
  };

  // Seasonality chart
  const seasonalityData = {
    labels: forecasting.seasonality.map(item => item.month),
    datasets: [
      {
        label: 'Seasonal Factor',
        data: forecasting.seasonality.map(item => item.factor),
        backgroundColor: forecasting.seasonality.map(item => 
          item.factor > 1 ? '#52c41a' : '#faad14'
        ),
      },
    ],
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <AnalyticsCard
            title="Revenue Projection"
            value={forecasting.projection}
            format="currency"
            trend="up"
            change={28.3}
            subtitle="annual growth"
          />
        </Col>
        <Col span={8}>
          <AnalyticsCard
            title="Forecast Confidence"
            value={forecasting.confidence * 100}
            format="percentage"
            trend="stable"
            change={2.1}
            subtitle="confidence level"
          />
        </Col>
        <Col span={8}>
          <AnalyticsCard
            title="Trend Direction"
            value={forecasting.trend === 'increasing' ? 100 : 50}
            format="percentage"
            trend="up"
            subtitle="upward trend strength"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <ChartContainer
            title={
              <span>
                <LineChartOutlined /> Revenue Forecasting (Next 6 Months)
              </span>
            }
            type="line"
            data={forecastingData}
            height={400}
          />
        </Col>
        <Col span={8}>
          <Card title="Forecast Details" style={{ height: '430px' }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Confidence Level</Text>
              <Progress 
                percent={forecasting.confidence * 100} 
                strokeColor="#1890ff"
                style={{ marginTop: 8 }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>Projected Annual Revenue</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginTop: 4 }}>
                ${forecasting.projection.toLocaleString()}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Growth Rate</Text>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a', marginTop: 4 }}>
                +28.3%
              </div>
            </div>

            <div>
              <Text strong>Peak Season</Text>
              <div style={{ marginTop: 4 }}>
                <Text>December - January</Text>
                <br />
                <Text type="secondary">18% above average</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <ChartContainer
            title="Seasonal Revenue Factors"
            type="bar"
            data={seasonalityData}
            height={300}
          />
        </Col>
        <Col span={12}>
          <Card title="Forecasting Assumptions" style={{ height: '330px' }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong>📈 Growth Drivers</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>25% increase in premium tier adoption</li>
                <li>Expansion to new geographic markets</li>
                <li>Enhanced service offerings</li>
              </ul>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <Text strong>⚠️ Risk Factors</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Economic downturn impact</li>
                <li>Increased competition</li>
                <li>Seasonal client churn</li>
              </ul>
            </div>

            <div>
              <Text strong>🎯 Key Milestones</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Q3: $100K monthly revenue</li>
                <li>Q4: 500 active clients</li>
                <li>Year-end: $625K annual revenue</li>
              </ul>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ForecastingChart; 